import { differenceInCalendarDays } from 'date-fns';
import type { PlanSummary } from '@timato/shared';
import { useAuth } from '@/auth/store';
import { useTeam } from '@/team/store';
import { usePlan } from './store';

type Membership = ReturnType<typeof useTeam.getState>['membership'];

/** Whole calendar days until `endsAt`: 0 on the last day, never negative. */
export const daysUntil = (endsAt: string, now = new Date()) => Math.max(0, differenceInCalendarDays(new Date(endsAt), now));

export type Trial = { daysLeft: number; team: boolean; to: string };

/**
 * The trial the person is on, if any (TW-13): the team's first, as the banner orders them, then their own 7 days.
 * A personal trial that ran out is not a trial any more but "sync is paused", which the banner says.
 */
export function trialOf(membership: Membership, summary: PlanSummary | undefined, now = new Date()): Trial | null {
  if (membership?.team.planStatus === 'trialing' && membership.team.trialEndsAt)
    return { team: true, daysLeft: daysUntil(membership.team.trialEndsAt, now), to: membership.role === 'owner' ? '/team/billing' : '/team' };
  if (summary && summary.via !== 'team' && summary.entitled && summary.source === 'trial' && summary.currentPeriodEnd)
    return { team: false, daysLeft: daysUntil(summary.currentPeriodEnd, now), to: '/settings/account' };
  return null;
}

/** "5 days left", "1 day left", "ends today". */
export const trialLeft = ({ daysLeft: n }: Pick<Trial, 'daysLeft'>) => (n === 0 ? 'ends today' : `${n} ${n === 1 ? 'day' : 'days'} left`);

export function useTrial(): Trial | null {
  const signedIn = useAuth((s) => s.status === 'signedIn');
  const membership = useTeam((s) => s.membership);
  const summary = usePlan((s) => s.summary);
  return signedIn ? trialOf(membership, summary) : null;
}
